import React from 'react';
import ReactDOM from 'react-dom/client';
import * as Sentry from '@sentry/react';
import './index.css';
import App from './App';
import { AuthProvider } from './auth/AuthProvider';
import { UserSessionProvider } from './auth/UserSessionContext';
import { ErrorBoundary } from './components/ErrorBoundary';
import { setupI18n } from './i18n';
import { cleanupServiceWorkersInProd } from './utils/serviceWorkerCleanup';
import { safeLocalStorage } from './lib/safeBrowserStorage';

// Sentry — solo en producción, sin PII
if (process.env.NODE_ENV === 'production') {
  Sentry.init({
    dsn: 'https://92cf409a74c22dac6717412b6a980b9d@o4511357652631552.ingest.us.sentry.io/4511357666656256',
    environment: 'production',
    release: process.env.REACT_APP_VERSION || 'unknown',
    sendDefaultPii: false,         // NO enviar IP ni headers con datos de usuario
    tracesSampleRate: 0.1,         // 10% de trazas (cuota gratuita conservada)
    replaysOnErrorSampleRate: 0,   // Sin replay (privacidad)
    replaysSessionSampleRate: 0,   // Sin replay
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed',
      'Non-Error promise rejection captured',
      /ChunkLoadError/,
    ],
  });
}

// Arrancar MSW: solo en desarrollo local
async function initMocks(){
  const isDev = process.env.NODE_ENV === 'development';
  if (isDev) {
    const disable = safeLocalStorage.getItem('msw.disabled') === '1';
    if(!disable){
      try {
        const { worker } = await import('./mocks/browser');
        await worker.start({ onUnhandledRequest: 'bypass' });
        try { window.mswReady = true; } catch {}
        console.info('[MSW] Worker iniciado en desarrollo');
      } catch (e){
        console.warn('[MSW] Error iniciando worker', e);
      }
    } else {
      console.info('[MSW] Deshabilitado vía localStorage msw.disabled=1');
    }
  } else {
    console.info('[MSW] No iniciado fuera de desarrollo');
  }
}

// Inicializar i18n con preferencia guardada, ?lang o idioma del navegador (fallback 'en')
const params = new URLSearchParams(window.location.search || '');
const urlLang = (params.get('lang') || '').toLowerCase().split('-')[0];
const saved = safeLocalStorage.getItem('i18n.lang');
const browserLang = (navigator.languages && navigator.languages[0]) || navigator.language || 'en';
const initialLang = (urlLang || saved || browserLang || 'en').toLowerCase().split('-')[0];
if (urlLang) safeLocalStorage.setItem('i18n.lang', initialLang);
setupI18n(initialLang);
cleanupServiceWorkersInProd();
initMocks();

const rootEl = document.getElementById('root');
const root = ReactDOM.createRoot(rootEl);
try {
  // StrictMode deshabilitado temporalmente para evitar conflictos con react-markdown v9
  // TODO: Rehabilitar cuando se actualice react-markdown o se encuentre solución
  root.render(
    <ErrorBoundary>
      <AuthProvider>
        <UserSessionProvider>
          <App />
        </UserSessionProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
} catch (e) {
  try { rootEl.innerHTML = `<pre data-testid="boot-error" style="color:#f55;white-space:pre-wrap;font-family:monospace">BOOT ERROR: ${e?.message}</pre>`; } catch {}
  console.error('BOOT_ERR', e);
}
